import { useQuery, useMutation } from '@apollo/client';
import { Box, Button, Input, Select, Stack, Text, Textarea } from '@sixfold/common-ui';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  NotificationTemplateMetadataListQuery,
  NotificationTemplateMetadataListQueryVariables,
  UpsertNotificationTemplateMetadataMutation,
  UpsertNotificationTemplateMetadataMutationVariables,
} from '../../lib/graphql';
import { NotificationPreviewList } from '../components/notification_preview_list';
import { notificationTemplateMetadataListQuery, upsertNotificationTemplateMetadataMutation } from '../graphql';
import { parseTemplateNames } from '../utils';

import styles from '../components/notification_preview.module.css';

interface PreviewData {
  subject: string;
  html: string;
}

export const NotificationPreviewContainer = () => {
  const [templateNames, setTemplateNames] = useState<string[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en');
  const [languageOptions, setLanguageOptions] = useState<{ value: string; label: string }[]>([
    { value: 'en', label: 'en' },
  ]);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [isEditingMetadata, setIsEditingMetadata] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [metadataSaveError, setMetadataSaveError] = useState<string | null>(null);

  const previewAbortControllerRef = useRef<AbortController | null>(null);

  const { data: metadataListData } = useQuery<
    NotificationTemplateMetadataListQuery,
    NotificationTemplateMetadataListQueryVariables
  >(notificationTemplateMetadataListQuery);

  const [upsertMetadata] = useMutation<
    UpsertNotificationTemplateMetadataMutation,
    UpsertNotificationTemplateMetadataMutationVariables
  >(upsertNotificationTemplateMetadataMutation);

  const metadataList = metadataListData?.notificationTemplateMetadataList ?? [];

  const selectedMetadata =
    selectedTemplate !== null ? (metadataList.find((m) => m.template_name === selectedTemplate) ?? null) : null;

  useEffect(() => {
    const controller = new AbortController();

    fetch('/api/notification-preview/templates', { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch templates: ${String(response.status)}`);
        }
        return response.text();
      })
      .then((html) => {
        const names = parseTemplateNames(html);
        setTemplateNames(names);
        setError(null);
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return;
        }
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to load notification templates. Ensure NOTIFICATION_PREVIEW_URL is configured.',
        );
      });

    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    fetch('/api/notification-preview/templates/languages', { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch languages: ${String(response.status)}`);
        }
        return response.json() as Promise<{ languages: string[] }>;
      })
      .then((data) => {
        const options = data.languages.map((lang) => ({ value: lang, label: lang }));
        setLanguageOptions(options);
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return;
        }
        console.warn('Failed to fetch languages, falling back to English only:', err);
        setLanguageOptions([{ value: 'en', label: 'en' }]);
      });

    return () => controller.abort();
  }, []);

  const fetchPreview = useCallback((templateName: string, language: string) => {
    if (previewAbortControllerRef.current !== null) {
      previewAbortControllerRef.current.abort();
    }

    const controller = new AbortController();
    previewAbortControllerRef.current = controller;

    fetch(`/api/notification-preview/templates/emails/${templateName}?format=json&lng=${language}`, {
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch preview: ${String(response.status)}`);
        }
        return response.json() as Promise<PreviewData>;
      })
      .then((data) => {
        setPreviewData(data);
        setPreviewError(null);
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return;
        }
        setPreviewError('Failed to load preview');
      });
  }, []);

  const handleSelectTemplate = useCallback(
    (name: string) => {
      setSelectedTemplate(name);
      setIsEditingMetadata(false);
      setMetadataSaveError(null);
      fetchPreview(name, selectedLanguage);
    },
    [fetchPreview, selectedLanguage],
  );

  const handleLanguageChange = useCallback(
    (option: { value: string; label: string } | null) => {
      if (option === null) {
        return;
      }
      setSelectedLanguage(option.value);
      if (selectedTemplate !== null) {
        fetchPreview(selectedTemplate, option.value);
      }
    },
    [fetchPreview, selectedTemplate],
  );

  const handleEditClick = useCallback(() => {
    setDraftTitle(selectedMetadata?.title ?? '');
    setDraftDescription(selectedMetadata?.description ?? '');
    setMetadataSaveError(null);
    setIsEditingMetadata(true);
  }, [selectedMetadata]);

  const handleCancelEdit = useCallback(() => {
    setIsEditingMetadata(false);
    setMetadataSaveError(null);
  }, []);

  const handleSaveMetadata = useCallback(async () => {
    if (selectedTemplate === null) {
      return;
    }
    setMetadataSaveError(null);
    try {
      await upsertMetadata({
        variables: {
          template_name: selectedTemplate,
          title: draftTitle !== '' ? draftTitle : null,
          description: draftDescription !== '' ? draftDescription : null,
        },
        refetchQueries: [{ query: notificationTemplateMetadataListQuery }],
      });
      setIsEditingMetadata(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save metadata';
      setMetadataSaveError(message);
    }
  }, [selectedTemplate, draftTitle, draftDescription, upsertMetadata]);

  if (error !== null) {
    return <div className={styles.error}>{error}</div>;
  }

  const currentTitle = selectedMetadata?.title ?? null;
  const currentDescription = selectedMetadata?.description ?? null;

  return (
    <div className={styles.container}>
      <NotificationPreviewList
        templateNames={templateNames}
        selectedTemplate={selectedTemplate}
        onSelectTemplate={handleSelectTemplate}
        metadataList={metadataList}
      />
      <div className={styles.previewArea}>
        {selectedTemplate !== null ? (
          <div className={styles.previewContainer}>
            <div className={styles.metadataSection}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap="medium">
                <Text className={styles.metadataHeading}>Backoffice-specific metadata</Text>
                {!isEditingMetadata && (
                  <Button kind="neutral" size="small" onClick={handleEditClick}>
                    Edit
                  </Button>
                )}
              </Stack>
              {isEditingMetadata ? (
                <div className={styles.editForm}>
                  <Input
                    label="Human-readable title"
                    name="metadata-title"
                    value={draftTitle}
                    onChange={(e) => setDraftTitle(e.value)}
                    placeholder="Enter template title"
                  />
                  <Textarea
                    label="Description"
                    name="metadata-description"
                    value={draftDescription}
                    onChange={(e) => setDraftDescription(e.value)}
                    placeholder="Enter template description"
                  />
                  {metadataSaveError !== null && (
                    <Text color="error" className={styles.editError}>
                      {metadataSaveError}
                    </Text>
                  )}
                  <div className={styles.buttonRow}>
                    <Button kind="primary" size="small" onClick={() => void handleSaveMetadata()}>
                      Save
                    </Button>
                    <Button kind="neutral" size="small" onClick={handleCancelEdit}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <Box paddingBlockStart="small">
                  <Text className={styles.sectionLabel}>Human-readable title</Text>
                  <Text>{currentTitle ?? <span className={styles.metadataPlaceholder}>No title</span>}</Text>
                  <Box paddingBlockStart="small">
                    <Text className={styles.sectionLabel}>Description</Text>
                    <Text>
                      {currentDescription ?? <span className={styles.metadataPlaceholder}>No description</span>}
                    </Text>
                  </Box>
                </Box>
              )}
            </div>

            <Stack direction="row" alignItems="end" gap="large">
              {previewData !== null && previewError === null ? (
                <Box flex={1}>
                  <Text className={styles.sectionLabel}>E-mail subject line</Text>
                  <Text>{previewData.subject}</Text>
                </Box>
              ) : null}
            </Stack>
            <Box paddingBlock="medium">
              <Text className={styles.sectionLabel}>Language</Text>
              <Select
                options={languageOptions}
                value={{ value: selectedLanguage, label: selectedLanguage }}
                onChange={handleLanguageChange}
              />
            </Box>
            {previewError !== null ? (
              <Box paddingBlock="large">
                <div className={styles.error}>{previewError}</div>
              </Box>
            ) : previewData !== null ? (
              <Box paddingBlock="large">
                <Text className={styles.sectionLabel}>Body</Text>
                <iframe
                  className={styles.previewIframe}
                  srcDoc={previewData.html}
                  title={`Email preview: ${selectedTemplate}`}
                  onLoad={(e) => {
                    const iframe = e.currentTarget;
                    const height = iframe.contentDocument?.documentElement.scrollHeight;
                    if (height !== undefined) {
                      iframe.style.height = `${String(height + 20)}px`;
                    }
                  }}
                />
              </Box>
            ) : null}
          </div>
        ) : (
          <div className={styles.placeholder}>Select a template to preview</div>
        )}
      </div>
    </div>
  );
};
